import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { I, Icon } from '../shared/ns-icons'

// ── Helpers ─────────────────────────────────────────────────────────────────

const timeAgo = (timestamp) => {
  const diff = Date.now() - new Date(timestamp).getTime()
  const mins = Math.floor(diff / 60000)
  const hrs  = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  if (mins < 1)  return 'now'
  if (mins < 60) return `${mins}m`
  if (hrs < 24)  return `${hrs}h`
  if (days < 7)  return `${days}d`
  return new Date(timestamp).toLocaleDateString()
}

const extractId = (url = '') => {
  const match = url.match(/\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i)
  return match ? match[1] : null
}

// Map a notification's type to an icon + tone (matches the redesign prototype)
const TYPE_META = {
  success:        { icon: 'rocket',  tone: 'success', who: 'Publishing' },
  site_published: { icon: 'rocket',  tone: 'success', who: 'Publishing' },
  warning:        { icon: 'cloud',   tone: 'warning', who: 'System' },
  error:          { icon: 'shield',  tone: 'danger',  who: 'System' },
  info:           { icon: 'message', tone: 'info',    who: 'Activity' },
  billing:        { icon: 'card',    tone: 'warning', who: 'Billing' },
  edit_request:   { icon: 'message', tone: 'accent',  who: 'Requests' },
  request:        { icon: 'message', tone: 'accent',  who: 'Requests' },
  support:        { icon: 'message', tone: 'info',    who: 'Support' },
  team:           { icon: 'users',   tone: 'accent',  who: 'Team' },
}
const metaFor = (n) => TYPE_META[n.type] || { icon: 'bell', tone: '', who: 'Activity' }

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'request', label: 'Requests' },
  { id: 'billing', label: 'Billing' },
  { id: 'system', label: 'System' },
]

const matchesFilter = (n, f) => {
  if (f === 'all') return true
  if (f === 'unread') return !n.is_read
  const hay = `${n.type} ${n.title} ${n.message}`.toLowerCase()
  if (f === 'request') return hay.includes('request')
  if (f === 'billing') return hay.includes('bill') || hay.includes('payment') || hay.includes('renew') || hay.includes('invoice')
  if (f === 'system') return ['info', 'warning', 'error', 'system', 'security'].includes(n.type) || hay.includes('sign-in') || hay.includes('backup')
  return true
}

const dayBucket = (ts) => {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = new Date(ts).getTime()
  if (t >= startOfToday) return 'Today'
  if (t >= startOfToday - 86400000) return 'Yesterday'
  if (t >= startOfToday - 7 * 86400000) return 'Earlier this week'
  return 'Older'
}

// ── Row ───────────────────────────────────────────────────────────────────────

function NotifRow({ n, onClick, onMarkRead }) {
  const meta = metaFor(n)
  return (
    <button onClick={onClick} style={{
      width: '100%', textAlign: 'left', display: 'flex', gap: 13, padding: '15px 16px',
      borderBottom: '1px solid var(--border)',
      borderLeft: !n.is_read ? '2px solid var(--accent)' : '2px solid transparent',
      background: !n.is_read ? 'var(--surface)' : 'transparent', transition: 'background 0.12s ease', cursor: 'pointer',
    }}>
      <span className={`icon-tile ${meta.tone || ''}`} style={{ width: 38, height: 38, fontSize: 16, flexShrink: 0 }}><Icon name={meta.icon} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4 }}>{n.title}</span>
          {!n.is_read && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 5 }} />}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)', flexShrink: 0, marginTop: 2 }}>{timeAgo(n.created_at)}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{n.message}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
          <span className="badge" style={{ fontSize: 10 }}>{meta.who}</span>
          {!n.is_read && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onMarkRead() }}
              className="btn btn-sm btn-ghost" style={{ fontSize: 11.5, padding: '3px 8px', color: 'var(--text-muted)' }}
            >
              <I.check style={{ fontSize: 11 }} /> Mark read
            </span>
          )}
          <span className="btn btn-sm btn-ghost" style={{ fontSize: 11.5, padding: '3px 8px', color: 'var(--accent-3)' }}>View <I.arrowRight style={{ fontSize: 11 }} /></span>
        </div>
      </div>
    </button>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const NotificationsPage = () => {
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useAuth()
  const role = user?.role

  const { data: notificationsData, isLoading } = useQuery(
    ['notifications-page'],
    () => apiService.get('/notifications?limit=100').then(r => r.data),
    { staleTime: 30000, retry: 1, refetchOnWindowFocus: false }
  )

  const invalidate = () => {
    queryClient.invalidateQueries('notifications-page')
    queryClient.invalidateQueries('notifications-unread-count')
    queryClient.invalidateQueries('notifications')
  }

  const markAsReadMutation = useMutation(
    (id) => apiService.patch(`/notifications/${id}/read`),
    { onSuccess: invalidate }
  )
  const markAllAsReadMutation = useMutation(
    () => apiService.patch('/notifications/mark-all-read'),
    { onSuccess: invalidate }
  )

  // ── Navigation (preserve original deep-link routing) ───────────────────────
  const getLink = (notification) => {
    const data = notification.data
      ? (typeof notification.data === 'string' ? (() => { try { return JSON.parse(notification.data) } catch { return {} } })() : notification.data)
      : {}
    const actionUrl = data.actionUrl || ''
    const title = (notification.title || '').toLowerCase()
    const message = (notification.message || '').toLowerCase()

    if (title.includes('edit request') || message.includes('edit request') || notification.type === 'edit_request' || actionUrl.includes('/requests')) {
      const id = data.requestId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/requests?open=${id}` : '/admin/requests'
      return id ? `/client/requests?open=${id}` : '/client/requests'
    }
    if (title.includes('support') || message.includes('support') || notification.type === 'support' || actionUrl.includes('/support')) {
      const id = data.ticketId || extractId(actionUrl)
      if (role === 'admin') return id ? `/admin/support?open=${id}` : '/admin/support'
      return id ? `/client/support?open=${id}` : '/client/support'
    }
    if (title.includes('site published') || message.includes('published') || notification.type === 'site_published') {
      if (role === 'admin') return data.siteId ? `/admin/sites/${data.siteId}` : '/admin/sites'
      return data.siteId ? `/client/sites/${data.siteId}` : '/client/sites'
    }
    if (title.includes('payment') || message.includes('payment') || title.includes('billing') || notification.type === 'billing') {
      if (role === 'admin') return data.clientId ? `/admin/billing?client=${data.clientId}` : '/admin/billing'
      return '/client/billing'
    }
    if (actionUrl && !actionUrl.includes('localhost') && !actionUrl.includes('/dashboard')) return actionUrl
    return role === 'admin' ? '/admin' : '/client'
  }

  const handleClick = (notification) => {
    if (!notification.is_read) markAsReadMutation.mutate(notification.id)
    const link = getLink(notification)
    if (link && link !== window.location.pathname) navigate(link)
  }

  // ── Derived state ──────────────────────────────────────────────────────────
  const all = notificationsData?.notifications || []
  const unread = all.filter(n => !n.is_read).length
  const q = search.trim().toLowerCase()

  const displayed = all.filter(n =>
    matchesFilter(n, filter) &&
    (!q || `${n.title} ${n.message}`.toLowerCase().includes(q))
  )

  // Group by day bucket, preserving API order (newest first)
  const ORDER = ['Today', 'Yesterday', 'Earlier this week', 'Older']
  const grouped = ORDER
    .map(label => ({ date: label, items: displayed.filter(n => dayBucket(n.created_at) === label) }))
    .filter(g => g.items.length > 0)

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Activity</div>
          <h1>Notifications</h1>
          <div className="subtitle">{unread} unread · across requests, billing, publishing and security</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={() => markAllAsReadMutation.mutate()} disabled={markAllAsReadMutation.isLoading || unread === 0}>
            <I.check /> Mark all read
          </button>
        </div>
      </div>

      <div className="page-body" style={{ maxWidth: 860 }}>
        <div className="filter-bar">
          <I.search style={{ color: 'var(--text-muted)', fontSize: 16 }} />
          <input className="input" placeholder="Search notifications…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="vdiv" style={{ height: 22 }} />
          <div className="segmented">
            {FILTERS.map(f => (
              <button key={f.id} className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
                {f.label}
                {f.id === 'unread' && unread > 0 && <span style={{ color: 'var(--accent-3)', marginLeft: 5, fontFamily: 'var(--font-mono)' }}>{unread}</span>}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="card card-pad" style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading notifications…</div>
        ) : grouped.length === 0 ? (
          <div className="card card-pad" style={{ padding: 48, textAlign: 'center' }}>
            <span className="icon-tile success" style={{ width: 52, height: 52, fontSize: 24, margin: '0 auto 14px' }}><I.check /></span>
            <div style={{ fontSize: 15, fontWeight: 600 }}>You're all caught up</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>No notifications match this filter.</div>
          </div>
        ) : (
          grouped.map(g => (
            <div key={g.date}>
              <div className="drawer-section-label" style={{ margin: '4px 2px 10px' }}>{g.date}</div>
              <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
                {g.items.map(n => (
                  <NotifRow key={n.id} n={n} onClick={() => handleClick(n)} onMarkRead={() => markAsReadMutation.mutate(n.id)} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}

export default NotificationsPage
